import React, { useState } from "react";
import { toast } from "sonner";
import { Pencil, Save, X } from "lucide-react";
import { api } from "../../lib/api";
import { PolicyAccessFields } from "./PolicyAccessFields";
import { fmtDate } from "./PolicyBadges";

const INPUT = "mt-1 w-full px-3 py-1.5 rounded-md border border-slate-200 text-sm";

export const PolicyMetadataCard = ({ policy, can, options, categories, user, onChanged }) => {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const isHM = user.access_role === "House Manager";
  const hmHomes = user.assigned_homes?.length ? user.assigned_homes : [user.assigned_home].filter(Boolean);

  const start = () => {
    setForm({ title: policy.title, category: policy.category, description: policy.description || "", owner: policy.owner || "",
      version_label: policy.version_label || "", effective_date: policy.effective_date || "", review_date: policy.review_date || "",
      home_scope: policy.home_scope || [], permitted_roles: policy.permitted_roles || [], all_roles: !!policy.all_roles });
    setEditing(true);
  };
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const save = async () => {
    setBusy(true);
    try {
      const body = { ...form };
      if (!can.manage_access) { delete body.permitted_roles; delete body.all_roles; if (!isHM) delete body.home_scope; }
      const { data } = await api.patch(`/policies/${policy.id}`, body);
      toast.success(data.changed.length ? `Updated: ${data.changed.join(", ")}` : "No changes");
      setEditing(false); onChanged();
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); } finally { setBusy(false); }
  };

  const Row = ({ k, label, children }) => (
    <div><dt className="text-[12.5px] uppercase tracking-wide text-slate-400">{label}</dt><dd className="text-sm text-slate-800 mt-0.5" data-testid={`policy-meta-${k}`}>{children}</dd></div>
  );

  return (
    <div className="nv-card p-5 space-y-4" data-testid="policy-metadata">
      <div className="flex items-center justify-between">
        <h3 className="text-h3">Details</h3>
        {can.edit && policy.status !== "Archived" && !editing && <button onClick={start} className="text-xs inline-flex items-center gap-1 px-2.5 py-1.5 rounded border border-slate-200" data-testid="policy-edit-button"><Pencil size={13} /> Edit</button>}
      </div>
      {!editing ? (
        <dl className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <Row k="category" label="Category">{policy.category}</Row>
          <Row k="owner" label="Owner">{policy.owner || "—"}</Row>
          <Row k="version" label="Version">{policy.version_label}</Row>
          <Row k="effective" label="Effective date">{fmtDate(policy.effective_date)}</Row>
          <Row k="review" label="Review date">{fmtDate(policy.review_date)}</Row>
          <Row k="uploader" label="Uploaded by">{policy.created_by_name} · {fmtDate(policy.created_at)}</Row>
          <Row k="scope" label="Applicable homes">{policy.home_scope?.length ? policy.home_scope.join(", ") : "All homes"}</Row>
          <Row k="audience" label="Permitted roles">{policy.all_roles ? "All roles (company-wide)" : (policy.permitted_roles || []).join(", ")}</Row>
          <Row k="approved" label="Approved">{policy.approved_by_name ? `${policy.approved_by_name} · ${fmtDate(policy.approved_at)}` : "—"}</Row>
          {policy.description && <div className="col-span-full"><dt className="text-[12.5px] uppercase tracking-wide text-slate-400">Description</dt><dd className="text-sm text-slate-700 mt-0.5" data-testid="policy-meta-description">{policy.description}</dd></div>}
        </dl>
      ) : (
        <div className="space-y-3" data-testid="policy-edit-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label className="text-xs font-semibold text-slate-600 md:col-span-2">Title<input value={form.title} onChange={set("title")} className={INPUT} data-testid="policy-edit-title" /></label>
            <label className="text-xs font-semibold text-slate-600">Category
              <select value={form.category} onChange={set("category")} className={INPUT} data-testid="policy-edit-category">{categories.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}</select></label>
            <label className="text-xs font-semibold text-slate-600">Owner<input value={form.owner} onChange={set("owner")} className={INPUT} data-testid="policy-edit-owner" /></label>
            <label className="text-xs font-semibold text-slate-600 md:col-span-2">Description<textarea rows={2} value={form.description} onChange={set("description")} className={INPUT} data-testid="policy-edit-description" /></label>
            <label className="text-xs font-semibold text-slate-600">Version label<input value={form.version_label} onChange={set("version_label")} className={INPUT} data-testid="policy-edit-version" /></label>
            <div className="grid grid-cols-2 gap-3">
              <label className="text-xs font-semibold text-slate-600">Effective<input type="date" value={form.effective_date || ""} onChange={set("effective_date")} className={INPUT} data-testid="policy-edit-effective" /></label>
              <label className="text-xs font-semibold text-slate-600">Review<input type="date" value={form.review_date || ""} onChange={set("review_date")} className={INPUT} data-testid="policy-edit-review" /></label>
            </div>
          </div>
          {(can.manage_access || isHM) ? (
            <PolicyAccessFields value={form} onChange={(v) => setForm((f) => ({ ...f, ...v }))} options={options}
              homes={isHM ? hmHomes : options.homes} canManageRoles={can.manage_access} compact />
          ) : (
            <p className="text-[12.5px] text-slate-500 bg-slate-50 border border-slate-200 rounded p-2" data-testid="policy-access-readonly-note">
              Applicable homes and permitted roles can only be changed by a policy manager (CEO, Project Manager, COO). Current audience: {policy.all_roles ? "All roles" : (policy.permitted_roles || []).join(", ")} · {policy.home_scope?.length ? policy.home_scope.join(", ") : "All homes"}.
            </p>
          )}
          <div className="flex gap-2 justify-end">
            <button onClick={() => setEditing(false)} className="text-sm px-3 py-1.5 rounded border border-slate-200 inline-flex items-center gap-1" data-testid="policy-edit-cancel"><X size={14} /> Cancel</button>
            <button onClick={save} disabled={busy} className="text-sm px-3 py-1.5 rounded bg-sky-700 text-white inline-flex items-center gap-1 disabled:opacity-50" data-testid="policy-edit-save"><Save size={14} /> Save</button>
          </div>
        </div>
      )}
    </div>
  );
};
